import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  FolderKanban,
  PlusCircle,
  Shield,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  Cpu,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator.jsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip.jsx';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
  { to: '/projects', icon: FolderKanban, label: 'Mes projets' },
  { to: '/projects/new', icon: PlusCircle, label: 'Nouveau projet' },
];

const ADMIN_ITEMS = [
  { to: '/admin', icon: Shield, label: 'Admin' },
  { to: '/admin/users', icon: Users, label: 'Utilisateurs' },
  { to: '/admin/settings', icon: Settings, label: 'Paramètres' },
];

function NavItem({ to, icon: Icon, label, collapsed }) {
  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-primary/10 text-primary'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
    } ${collapsed ? 'justify-center px-2' : ''}`;

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <NavLink to={to} className={linkClass} end={to === '/admin'}>
            <Icon className="w-5 h-5 shrink-0" />
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <NavLink to={to} className={linkClass} end={to === '/admin'}>
      <Icon className="w-5 h-5 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

export default function Sidebar({ collapsed, onToggle }) {
  const { isAdmin } = useAuth();

  return (
    <TooltipProvider>
      <aside
        className={`fixed left-0 top-0 h-screen bg-card border-r border-border flex flex-col transition-all duration-200 z-30 ${
          collapsed ? 'w-[var(--sidebar-width-collapsed)]' : 'w-[var(--sidebar-width)]'
        }`}
      >
        {/* Logo */}
        <div className={`flex items-center h-14 px-4 border-b border-border ${collapsed ? 'justify-center px-2' : ''}`}>
          <Cpu className="w-6 h-6 text-primary shrink-0" />
          {!collapsed && (
            <span className="ml-2 font-semibold text-foreground text-sm tracking-tight">
              AI Orchestrator
            </span>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavItem key={item.to} {...item} collapsed={collapsed} />
          ))}

          {isAdmin && (
            <>
              <Separator className="my-3" />
              <div className={`px-3 mb-2 ${collapsed ? 'hidden' : ''}`}>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Administration
                </span>
              </div>
              {ADMIN_ITEMS.map((item) => (
                <NavItem key={item.to} {...item} collapsed={collapsed} />
              ))}
            </>
          )}
        </nav>

        {/* Toggle */}
        <div className="p-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="w-full flex items-center justify-center"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
